import { useEffect, useMemo, useState } from "react";
import api, { formatError } from "../lib/api";
import DeleteWithReasonDialog from "../components/DeleteWithReasonDialog";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/GradientCard";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { tabAccessibleFor } from "../lib/modules";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { Store, Eye, EyeOff, UserPlus, FileText, Building2, IndianRupee, FolderPlus, ExternalLink, Trash2, KeyRound, Copy, Pencil } from "lucide-react";
import { useGlobalViewMode } from "../components/ViewModeToggle";

const STATUS_TONE = {
  "Request Raised": "bg-amber-500 text-white",
  Working: "bg-blue-600 text-white",
  Completed: "bg-emerald-600 text-white",
  "Order Done": "bg-emerald-700 text-white",
  Cancelled: "bg-rose-600 text-white",
};
const GEM_LOCKED_STATUSES = new Set(["Order Done", "Cancelled"]);

export default function GeMWork() {
  const { user } = useAuth();
  const isMaster = user?.role === "master_admin";
  // GeM Admin = anyone with the `gem_work` module in their explicit modules
  // list. Note: getting auto-granted on assignment also makes them a GeM
  // Admin for assigned-row operations BUT does NOT give them visibility into
  // the "All Requests" tab — that is restricted to Master Admin + explicit
  // tab-access (see canSeeAll below).
  const isGemAdmin = isMaster || (user?.modules || []).includes("gem_work");
  // Only Master Admin OR a user with WRITE access on the gem_work module can
  // assign a GeM team member to a request. Edit-level (auto-granted on
  // assignment) is intentionally insufficient.
  const canAssignTeam = isMaster || (((user?.module_access_levels || {}).gem_work || "").toLowerCase() === "write");

  // Per-user tab-access map (Master only fully populates this; non-admins probe their access).
  const [extraAccess, setExtraAccess] = useState({
    all: false, user_request: false, my_raised: false,
    tender_found: false, tender_working: false, tender_filled: false,
    tender_unfilled: false, tender_result: false,
    gem_credentials: false,
  });
  // All Requests is strictly Master Admin OR explicit tab grant — being an
  // assignee (gem_work module auto-grant) is NOT enough.
  const canSeeAll = isMaster || extraAccess.all;
  const canSeeUserRequest = isGemAdmin || extraAccess.user_request;
  // Every authenticated user can see "My Raised" (only their own data).
  const canSeeMyRaised = true;
  // Tender Work parent tab is visible if Master OR user has any tender sub-tab access.
  const canSeeTenders = isMaster
    || extraAccess.tender_found || extraAccess.tender_working || extraAccess.tender_filled
    || extraAccess.tender_unfilled || extraAccess.tender_result;
  // GeM Credentials vault — strictly Master + explicit grant.
  const canSeeCreds = isMaster || extraAccess.gem_credentials;

  const [tab, setTab] = useState("my_raised");
  const [assigned, setAssigned] = useState([]);   // User Request — assigned to me
  const [mine, setMine] = useState([]);            // My Raised — by me
  const [all, setAll] = useState([]);              // Master / GeM Admin only
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [activeReq, setActiveReq] = useState(null);
  const [activeRemark, setActiveRemark] = useState("");
  const [assignTarget, setAssignTarget] = useState(null);
  const [employees, setEmployees] = useState([]);
  // v109.9.40 — Tab Access dialog removed; stub retained to avoid rippling changes.
  const tabAccessOpen = false; const setTabAccessOpen = () => {};
  void tabAccessOpen; void setTabAccessOpen;
  const [commentsReq, setCommentsReq] = useState(null);  // row-level Comments dialog target

  useEffect(() => {
    (async () => {
      const probes = await Promise.allSettled([
        api.get("/gem-requests?scope=all"),
        api.get("/gem-requests?scope=assigned"),
        api.get("/tenders?stage=Found"),
        api.get("/tenders?stage=Working"),
        api.get("/tenders?stage=Filled"),
        api.get("/tenders?stage=Unfilled"),
        api.get("/tenders?stage=Result"),
        api.get("/gem-credentials"),
      ]);
      setExtraAccess({
        all: probes[0].status === "fulfilled",
        user_request: probes[1].status === "fulfilled",
        my_raised: true,
        tender_found: probes[2].status === "fulfilled",
        tender_working: probes[3].status === "fulfilled",
        tender_filled: probes[4].status === "fulfilled",
        tender_unfilled: probes[5].status === "fulfilled",
        tender_result: probes[6].status === "fulfilled",
        gem_credentials: probes[7].status === "fulfilled",
      });
      if (probes[1].status === "fulfilled") setAssigned(probes[1].value.data || []);
      if (probes[0].status === "fulfilled") setAll(probes[0].value.data || []);
    })();
    loadMine();
    if (isGemAdmin) loadEmployees();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadMine = async () => {
    try {
      const { data } = await api.get("/gem-requests?scope=mine");
      setMine(data || []);
    } catch (e) { /* silent */ }
  };

  const loadAll = async () => {
    try {
      const { data } = await api.get("/gem-requests?scope=all");
      setAll(data || []);
    } catch (e) { /* silent */ }
  };

  const loadAssigned = async () => {
    try {
      const { data } = await api.get("/gem-requests?scope=assigned");
      setAssigned(data || []);
    } catch (e) { /* silent */ }
  };

  const loadEmployees = async () => {
    try {
      const { data } = await api.get("/documentation/assignable-users");
      setEmployees(data || []);
    } catch (e) { /* silent */ }
  };

  const openDetail = async (r) => {
    try {
      const { data } = await api.get(`/gem-requests/${r.id}`);
      setActiveReq(data);
      setActiveRemark("");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const changeStatus = async (status) => {
    if (!activeReq) return;
    try {
      const { data } = await api.patch(`/gem-requests/${activeReq.id}/status`, { status, remark: activeRemark });
      setActiveReq(data);
      setActiveRemark("");
      toast.success(`Marked ${status}`);
      loadMine(); loadAssigned(); if (canSeeAll) loadAll();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const addRemark = async () => {
    if (!activeReq || !activeRemark.trim()) return;
    try {
      const { data } = await api.post(`/gem-requests/${activeReq.id}/logs`, { remark: activeRemark });
      setActiveReq(data);
      setActiveRemark("");
      toast.success("Remark added");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const submitAssign = async (uid) => {
    if (!assignTarget) return;
    try {
      const { data } = await api.post(`/gem-requests/${assignTarget.id}/assign`, { user_id: uid });
      toast.success(`Assigned to ${data.assigned_to_name}`);
      setAssignTarget(null);
      loadAssigned(); if (canSeeAll) loadAll();
      if (activeReq?.id === assignTarget.id) setActiveReq(data);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  // Row-level stage change (no dialog). Authorization is enforced on the
  // server — UI just hands the user the dropdown.
  const rowChangeStatus = async (r, status) => {
    if (!status || status === r.status) return;
    try {
      await api.patch(`/gem-requests/${r.id}/status`, { status });
      toast.success(`Marked ${status}`);
      loadMine(); loadAssigned(); if (canSeeAll) loadAll();
      if (activeReq?.id === r.id) {
        try { const { data } = await api.get(`/gem-requests/${r.id}`); setActiveReq(data); } catch (e) { /* silent */ }
      }
    } catch (e) {
      const code = e?.response?.status;
      if (code === 423) toast.error("Locked — only Master Admin can change this stage.");
      else toast.error(formatError(e.response?.data?.detail));
    }
  };

  // Filter helpers shared by all tabs
  const applyFilter = (list) => list.filter((r) => {
    if (statusFilter !== "all" && r.status !== statusFilter) return false;
    const q = search.trim().toLowerCase();
    if (q && ![r.client_name, r.u_dise, r.sales_owner_name, r.assigned_to_name, r.gem_option, r.order_kind].some((v) => String(v || "").toLowerCase().includes(q))) return false;
    return true;
  });

  const currentRows = useMemo(() => {
    if (tab === "user_request") return applyFilter(assigned);
    if (tab === "my_raised") return applyFilter(mine);
    if (tab === "all") return applyFilter(all);
    return [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, assigned, mine, all, statusFilter, search]);

  return (
    <div className="space-y-6">
      <PageHeader
        moduleKey="gem_work"
        eyebrow="Sales workflow"
        title="GeM Work"
        icon={Store}
        subtitle="GeM (Government e-Marketplace) order support — clients flagged from CRM Reporting flow here for the GeM team to action."
        actions={null}
      />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          {canSeeMyRaised && tabAccessibleFor(user, "gem_work", "my_raised") && (<TabsTrigger value="my_raised" data-testid="gem-tab-my-raised">My Raised ({mine.length})</TabsTrigger>)}
          {canSeeUserRequest && tabAccessibleFor(user, "gem_work", "user_request") && (<TabsTrigger value="user_request" data-testid="gem-tab-user-request">User Request ({assigned.length})</TabsTrigger>)}
          {canSeeAll && tabAccessibleFor(user, "gem_work", "all") && (<TabsTrigger value="all" data-testid="gem-tab-all">All Requests ({all.length})</TabsTrigger>)}
          {canSeeTenders && tabAccessibleFor(user, "gem_work", "tender_work") && (<TabsTrigger value="tender_work" data-testid="gem-tab-tender-work">Tender Work</TabsTrigger>)}
          {canSeeCreds && tabAccessibleFor(user, "gem_work", "gem_credentials") && (<TabsTrigger value="gem_credentials" data-testid="gem-tab-credentials">Credentials</TabsTrigger>)}
        </TabsList>

        {/* Filter row only visible on GeM Request tabs — Tender Work + Credentials have their own filters */}
        {tab !== "tender_work" && tab !== "gem_credentials" && (
          <div className="mt-4 flex items-center gap-2 flex-wrap">
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by client, U-DISE, owner…" className="h-9 max-w-sm" data-testid="gem-search" />
            <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Status</span>
            {["all", "Request Raised", "Working", "Completed", "Order Done", "Cancelled"].map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`h-8 px-3 text-[11.5px] font-semibold rounded-full border transition-all ${statusFilter === s ? (STATUS_TONE[s] ? STATUS_TONE[s] + " border-transparent" : "bg-violet-600 border-violet-600 text-white") : "bg-white border-slate-200 text-slate-700"}`}
                data-testid={`gem-status-${s.replace(/\s+/g, "-")}`}
              >
                {s === "all" ? "All" : s}
              </button>
            ))}
          </div>
        )}

        <TabsContent value="my_raised" className="mt-3"><GemTable rows={currentRows} onOpen={openDetail} onComments={setCommentsReq} onRowStage={rowChangeStatus} userId={user?.id} isMaster={isMaster} showOwner={false} /></TabsContent>
        {canSeeUserRequest && <TabsContent value="user_request" className="mt-3"><GemTable rows={currentRows} onOpen={openDetail} onComments={setCommentsReq} onRowStage={rowChangeStatus} userId={user?.id} isMaster={isMaster} showOwner /></TabsContent>}
        {canSeeAll && <TabsContent value="all" className="mt-3"><GemTable rows={currentRows} onOpen={openDetail} onComments={setCommentsReq} onRowStage={rowChangeStatus} userId={user?.id} isMaster={isMaster} showOwner /></TabsContent>}
        {canSeeTenders && (
          <TabsContent value="tender_work" className="mt-3">
            <TenderWorkSection
              isMaster={isMaster}
              access={{
                found: extraAccess.tender_found,
                working: extraAccess.tender_working,
                filled: extraAccess.tender_filled,
                unfilled: extraAccess.tender_unfilled,
                result: extraAccess.tender_result,
              }}
            />
          </TabsContent>
        )}
        {canSeeCreds && (
          <TabsContent value="gem_credentials" className="mt-3">
            <GemCredentialsSection isMaster={isMaster} />
          </TabsContent>
        )}
      </Tabs>

      {/* Detail / action dialog */}
      <Dialog open={!!activeReq} onOpenChange={(o) => { if (!o) setActiveReq(null); }}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="gem-detail-dialog">
          <DialogHeader>
            <DialogTitle className="heading flex items-center gap-2">
              GeM Request · {activeReq?.client_name}
              {activeReq?.status && (<Badge className={STATUS_TONE[activeReq.status] || "bg-slate-100 text-slate-700"}>{activeReq.status}</Badge>)}
            </DialogTitle>
            <div className="text-[11px] text-slate-500 mt-1">U-DISE {activeReq?.u_dise || "—"} · raised {activeReq?.created_at ? new Date(activeReq.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—"}</div>
          </DialogHeader>

          {activeReq && (
            <div className="space-y-4">
              <Section title="Request">
                <Row k="Needs Help Placing Order?" v={activeReq.needs_help ? "Yes" : "No"} />
                <Row k="GeM Option" v={activeReq.gem_option === "Other" ? `Other — ${activeReq.gem_option_other || "—"}` : activeReq.gem_option} />
                <Row k="Order Kind" v={activeReq.order_kind} />
                {activeReq.order_kind === "Product" && <Row k="Product" v={activeReq.product_description || "—"} />}
                {activeReq.order_kind === "Service" && <Row k="Service" v={activeReq.service_description || "—"} />}
                <Row k="Order Value (₹)" v={<span className="font-mono"><IndianRupee size={11} className="inline" /> {Number(activeReq.order_value || 0).toLocaleString("en-IN")}</span>} />
              </Section>

              <Section title="Client Details">
                <Row k="Client Name" v={activeReq.client_details?.client_name || activeReq.client_name} />
                <Row k="Type" v={activeReq.client_details?.client_type || (activeReq.client_details?.client_types || []).join(", ") || "—"} />
                <Row k="District / State" v={`${activeReq.client_details?.district || "—"}${activeReq.client_details?.state ? ` · ${activeReq.client_details.state}` : ""}`} />
                <Row k="Client Email" v={activeReq.client_details?.client_email || "—"} />
                <Row k="Client Phone" v={activeReq.client_details?.client_phone || activeReq.client_details?.client_mobile || "—"} />
              </Section>

              <Section title="Sales Owner">
                <Row k="Name" v={activeReq.sales_owner_name || "—"} />
                <Row k="Email" v={activeReq.sales_owner_email || "—"} />
                <Row k="Phone" v={activeReq.sales_owner_phone || "—"} />
              </Section>

              <Section title="GeM Team">
                <Row k="Assigned To" v={activeReq.assigned_to_name || <span className="text-amber-700 italic">— Not assigned yet</span>} />
                {canAssignTeam && (
                  <Button size="sm" variant="outline" className="h-8 px-2.5 text-[11px] mt-2" onClick={() => setAssignTarget(activeReq)} data-testid="gem-detail-assign">
                    <UserPlus size={11} className="mr-1" /> {activeReq.assigned_to_id ? "Re-assign" : "Assign"}
                  </Button>
                )}
              </Section>

              {/* COMMENTS — pulled fully out of any Section card. Anyone with
                  access to the request (sales owner, assigned GeM user, or
                  Admin) can drop a comment on the audit trail. */}
              {(() => {
                const isOwner = activeReq.sales_owner_id === user?.id;
                const isAssignee = activeReq.assigned_to_id === user?.id;
                if (!(isGemAdmin || isAssignee || isOwner)) return null;
                return (
                  <div className="space-y-2 mt-1" data-testid="gem-comments-block">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Comments</div>
                    <textarea
                      value={activeRemark}
                      onChange={(e) => setActiveRemark(e.target.value)}
                      placeholder="Add a comment / update on this request…"
                      rows={3}
                      className="w-full text-sm border border-slate-200 rounded-md px-3 py-2 bg-white dark:bg-slate-900 dark:border-slate-700"
                      data-testid="gem-remark-input"
                    />
                    <div>
                      <Button size="sm" variant="outline" onClick={addRemark} disabled={!activeRemark.trim()} className="h-9 text-[12px]" data-testid="gem-add-remark">Post Comment</Button>
                    </div>
                  </div>
                );
              })()}

              {/* STAGE controls are now exposed as a per-row dropdown in the
                  table (see GemTable). The detail dialog no longer carries
                  the Stage block — it stays read-only summary + comments. */}

              <Section title="Activity Log">
                <div className="space-y-2">
                  {(activeReq.logs || []).slice().reverse().map((l, i) => (
                    <div key={i} className="rounded-md border border-slate-200 px-3 py-2 text-[12px]" data-testid={`gem-log-${i}`}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-semibold text-slate-800">{l.by_name || "—"}</div>
                        <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100 text-[10px]">{(l.action || "log").replaceAll("_", " ")}</Badge>
                      </div>
                      <div className="text-[10.5px] text-slate-500 mono mt-0.5">{l.at ? new Date(l.at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—"}</div>
                      {l.from !== undefined && l.to !== undefined && (
                        <div className="text-[11.5px] mt-1">
                          <span className="text-rose-600 line-through">{l.from || "—"}</span> → <span className="text-emerald-700 font-semibold">{l.to || "—"}</span>
                        </div>
                      )}
                      {l.remark && <div className="text-[12px] text-slate-700 mt-1 whitespace-pre-wrap">{l.remark}</div>}
                    </div>
                  ))}
                </div>
              </Section>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setActiveReq(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign dialog */}
      <Dialog open={!!assignTarget} onOpenChange={(o) => { if (!o) setAssignTarget(null); }}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto" data-testid="gem-assign-dialog">
          <DialogHeader>
            <DialogTitle className="heading">Assign GeM Team Member</DialogTitle>
            <div className="text-[11px] text-slate-500 mt-1">{assignTarget?.client_name} · {assignTarget?.gem_option}</div>
          </DialogHeader>
          <div className="text-[11px] bg-violet-50 border border-violet-200 rounded-md px-3 py-2 my-2 text-violet-800">
            The selected user is auto-granted the GeM Work module at <strong>Edit</strong> access.
          </div>
          <div className="space-y-1 max-h-[50vh] overflow-y-auto">
            {employees.length === 0 && <div className="text-xs text-slate-400 italic py-4 text-center">No users available.</div>}
            {employees.map((u) => (
              <button
                key={u.id}
                onClick={() => submitAssign(u.id)}
                className="w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-md border-2 border-slate-200 hover:border-violet-400 hover:bg-violet-50 transition-all"
                data-testid={`gem-assign-pick-${u.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-900">{u.name}</div>
                  <div className="text-[11px] text-slate-500">{u.role}{u.department ? ` · ${u.department}` : ""}</div>
                </div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* v109.9.40 — GemTabAccessDialog removed per user request */}

      {/* Row-level threaded Comments dialog. Reachable from every row via the
          new Comment button in My Raised / User Request / All Requests tabs. */}
      <GemCommentsDialog
        request={commentsReq}
        userId={user?.id}
        onOpenChange={(o) => { if (!o) setCommentsReq(null); }}
        onUpdated={(updated) => { setCommentsReq(updated); if (activeReq?.id === updated.id) setActiveReq(updated); }}
      />
    </div>
  );
}

function GemTable({ rows, onOpen, onComments, onRowStage, userId, isMaster, showOwner }) {
  const [viewMode] = useGlobalViewMode();
  const STAGES = ["Request Raised", "Working", "Completed", "Order Done", "Cancelled"];
  if (rows.length === 0) return <div className="pk-card p-12 text-center text-slate-400 text-sm">No GeM requests here.</div>;
  if (viewMode === "card") {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="gem-cards">
        {rows.map((r) => {
          const isAssignee = r.assigned_to_id === userId;
          const canChangeStage = isMaster || isAssignee;
          const locked = ["Order Done", "Cancelled"].includes(r.status) && !isMaster;
          return (
            <div key={r.id} className="pk-card p-4 hover:shadow-md transition-shadow" data-testid={`gem-card-${r.id}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <Building2 size={13} className="text-indigo-500 shrink-0" />
                    <div className="font-bold text-slate-900 truncate">{r.client_name}</div>
                  </div>
                  <div className="text-[10px] text-slate-500 mono mt-0.5">U-DISE {r.u_dise || "—"}</div>
                </div>
                <Badge className={(STATUS_TONE[r.status] || "bg-slate-100 text-slate-700") + " text-[10px] shrink-0"}>{r.status}</Badge>
              </div>
              <div className="grid grid-cols-2 gap-2 text-[11.5px] mb-2">
                <div><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">GeM Option</div><div className="text-slate-800 truncate">{r.gem_option === "Other" ? `Other — ${r.gem_option_other || "—"}` : r.gem_option}</div></div>
                <div><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Kind</div><div className="text-slate-800">{r.order_kind}</div></div>
                <div><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Value</div><div className="mono font-semibold text-slate-800">₹ {Number(r.order_value || 0).toLocaleString("en-IN")}</div></div>
                {showOwner && <div><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Sales Owner</div><div className="text-slate-800 truncate">{r.sales_owner_name || "—"}</div></div>}
                <div className="col-span-2"><div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold">Assignee</div><div className="text-slate-800">{r.assigned_to_name || <span className="text-amber-700 italic">— Unassigned</span>}</div></div>
              </div>
              <div className="flex flex-wrap gap-1.5 pt-2 border-t border-slate-100">
                {canChangeStage && (
                  <select
                    value=""
                    onChange={(e) => { const v = e.target.value; if (v) { onRowStage(r, v); e.target.value = ""; } }}
                    disabled={locked}
                    className="h-7 text-[10.5px] border border-slate-200 rounded-md px-1.5 bg-white dark:bg-slate-900 dark:border-slate-700 disabled:opacity-50"
                    data-testid={`gem-card-stage-${r.id}`}
                  >
                    <option value="">{locked ? "🔒 Locked" : "Change…"}</option>
                    {STAGES.filter((s) => s !== r.status).map((s) => (<option key={s} value={s}>Mark {s}</option>))}
                  </select>
                )}
                <Button size="sm" variant="outline" className="h-7 px-2 text-[10.5px]" onClick={() => onComments(r)} data-testid={`gem-comments-${r.id}`}>
                  <FileText size={10} className="mr-1" /> Comment
                </Button>
                <Button size="sm" variant="outline" className="h-7 px-2 text-[10.5px]" onClick={() => onOpen(r)} data-testid={`gem-open-${r.id}`}>
                  <Eye size={10} className="mr-1" /> Open
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    );
  }
  return (
    <div className="pk-card overflow-x-auto">
      <table className="w-full text-sm responsive-table">
        <thead className="border-b border-slate-200 bg-slate-50/60">
          <tr>
            <th className="px-4 py-3 text-left pk-table-header">Client</th>
            <th className="px-4 py-3 text-left pk-table-header">GeM Option</th>
            <th className="px-4 py-3 text-left pk-table-header">Kind</th>
            <th className="px-4 py-3 text-right pk-table-header">Order Value</th>
            <th className="px-4 py-3 text-left pk-table-header">Stage</th>
            {showOwner && <th className="px-4 py-3 text-left pk-table-header">Sales Owner</th>}
            <th className="px-4 py-3 text-left pk-table-header">Assignee</th>
            <th className="px-4 py-3 text-right pk-table-header">Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (<tr><td colSpan={showOwner ? 8 : 7} className="text-center py-10 text-slate-400 text-sm">No GeM requests here.</td></tr>)}
          {rows.map((r) => {
            const isAssignee = r.assigned_to_id === userId;
            const canChangeStage = isMaster || isAssignee;
            const locked = ["Order Done", "Cancelled"].includes(r.status) && !isMaster;
            return (
              <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`gem-row-${r.id}`}>
                <td className="px-4 py-3" data-label="Client">
                  <div className="flex items-center gap-2"><Building2 size={14} className="text-indigo-500" /><div className="font-semibold text-slate-900">{r.client_name}</div></div>
                  <div className="text-[10px] text-slate-500 mono">U-DISE {r.u_dise || "—"}</div>
                </td>
                <td className="px-4 py-3 text-[12px]" data-label="GeM Option">{r.gem_option === "Other" ? `Other — ${r.gem_option_other || "—"}` : r.gem_option}</td>
                <td className="px-4 py-3 text-[12px]" data-label="Kind">{r.order_kind}</td>
                <td className="px-4 py-3 text-right mono text-[12px]" data-label="Value">₹ {Number(r.order_value || 0).toLocaleString("en-IN")}</td>
                <td className="px-4 py-3" data-label="Stage">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className={(STATUS_TONE[r.status] || "bg-slate-100 text-slate-700") + " text-[10px]"}>{r.status}</Badge>
                    {canChangeStage && (
                      <select
                        value=""
                        onChange={(e) => { const v = e.target.value; if (v) { onRowStage(r, v); e.target.value = ""; } }}
                        disabled={locked}
                        title={locked ? "Locked — only Master Admin can change" : "Change stage"}
                        className="h-7 text-[10.5px] border border-slate-200 rounded-md px-1.5 bg-white dark:bg-slate-900 dark:border-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid={`gem-row-stage-${r.id}`}
                      >
                        <option value="">{locked ? "🔒 Locked" : "Change…"}</option>
                        {STAGES.filter((s) => s !== r.status).map((s) => (<option key={s} value={s}>Mark {s}</option>))}
                      </select>
                    )}
                  </div>
                </td>
                {showOwner && <td className="px-4 py-3 text-[12px]" data-label="Owner">{r.sales_owner_name || "—"}</td>}
                <td className="px-4 py-3 text-[12px]" data-label="Assignee">{r.assigned_to_name || <span className="text-amber-700 italic">— Unassigned</span>}</td>
                <td className="px-4 py-3 text-right" data-label="Actions">
                  <div className="flex items-center justify-end gap-1.5 flex-wrap">
                    <Button size="sm" variant="outline" className="h-8 px-2.5 text-[11px]" onClick={() => onComments(r)} data-testid={`gem-comments-${r.id}`}>
                      <FileText size={11} className="mr-1" /> Comment
                    </Button>
                    <Button size="sm" variant="outline" className="h-8 px-2.5 text-[11px]" onClick={() => onOpen(r)} data-testid={`gem-open-${r.id}`}>
                      <Eye size={11} className="mr-1" /> Open
                    </Button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="rounded-md border border-slate-200 bg-white px-4 py-3">
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2">{title}</div>
      {children}
    </div>
  );
}
function Row({ k, v }) {
  return (
    <div className="grid grid-cols-3 gap-2 text-[12.5px] py-1">
      <div className="col-span-1 text-slate-500">{k}</div>
      <div className="col-span-2 text-slate-900 font-medium">{v}</div>
    </div>
  );
}

/* ============================================================================
 * Threaded Comments dialog. Each comment can have an optional parent_id so we
 * can render replies inline. Both the Sales Owner who raised the request and
 * the assigned GeM team member (+ Admin) can post / reply.
 * ==========================================================================*/
function GemCommentsDialog({ request, userId, onOpenChange, onUpdated }) {
  const [draft, setDraft] = useState("");
  const [replyTo, setReplyTo] = useState(null);    // comment_id we're replying to
  const [busy, setBusy] = useState(false);
  const open = !!request;

  // Reset transient state when dialog closes / target changes
  useEffect(() => { setDraft(""); setReplyTo(null); }, [request?.id]);

  if (!open) return null;
  // Build a thread tree from the flat logs[] array. Non-remark logs (status,
  // assign etc.) are skipped — they live in the Activity Log section.
  const remarks = (request.logs || []).filter((l) => l.action === "remark");
  const children = {}; // parent_id -> [comments]
  const roots = [];
  remarks.forEach((c) => {
    if (c.parent_id) (children[c.parent_id] ||= []).push(c);
    else roots.push(c);
  });
  // Legacy remarks without comment_id (pre-v58) are treated as roots.
  remarks.forEach((c) => { if (!c.comment_id && !c.parent_id && !roots.includes(c)) roots.push(c); });

  const submit = async () => {
    if (!draft.trim()) return;
    setBusy(true);
    try {
      const payload = { remark: draft.trim() };
      if (replyTo) payload.parent_id = replyTo;
      const { data } = await api.post(`/gem-requests/${request.id}/logs`, payload);
      toast.success(replyTo ? "Reply posted" : "Comment posted");
      setDraft("");
      setReplyTo(null);
      onUpdated?.(data);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const renderComment = (c, depth = 0) => (
    <div key={c.comment_id || `${c.by_id}-${c.at}`} className={`rounded-md border border-slate-200 bg-white px-3 py-2 text-[12px]`} style={{ marginLeft: depth * 18 }} data-testid={`gem-comment-${c.comment_id || ""}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="font-semibold text-slate-800">{c.by_name || "—"}{c.by_id === userId && <span className="ml-1 text-[10px] text-slate-400">(you)</span>}</div>
        <div className="text-[10px] text-slate-500 mono">{c.at ? new Date(c.at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—"}</div>
      </div>
      <div className="text-[12.5px] text-slate-700 mt-1 whitespace-pre-wrap">{c.remark}</div>
      {c.comment_id && (
        <button
          onClick={() => setReplyTo(c.comment_id)}
          className="mt-1.5 text-[11px] text-indigo-600 hover:underline"
          data-testid={`gem-reply-btn-${c.comment_id}`}
        >
          ↳ Reply
        </button>
      )}
      {(children[c.comment_id] || []).map((child) => renderComment(child, depth + 1))}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="gem-comments-dialog">
        <DialogHeader>
          <DialogTitle className="heading flex items-center gap-2">Comments · {request.client_name}</DialogTitle>
          <div className="text-[11px] text-slate-500 mt-1">U-DISE {request.u_dise || "—"} · {remarks.length} comment{remarks.length === 1 ? "" : "s"}</div>
        </DialogHeader>
        <div className="space-y-2 mt-2 max-h-[55vh] overflow-y-auto pr-1">
          {roots.length === 0 && (<div className="py-6 text-center text-xs text-slate-400 italic">No comments yet. Start the conversation below.</div>)}
          {roots.map((c) => renderComment(c, 0))}
        </div>

        <div className="border-t pt-3 mt-3 space-y-2">
          {replyTo && (
            <div className="text-[11px] text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-md px-2 py-1.5 flex items-center justify-between">
              <span>Replying to a comment</span>
              <button onClick={() => setReplyTo(null)} className="text-[11px] underline" data-testid="gem-cancel-reply">cancel</button>
            </div>
          )}
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={replyTo ? "Write your reply…" : "Add a comment…"}
            rows={3}
            className="w-full text-sm border border-slate-200 rounded-md px-3 py-2 bg-white dark:bg-slate-900 dark:border-slate-700"
            data-testid="gem-comments-draft"
          />
          <div className="flex items-center justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
            <Button onClick={submit} disabled={busy || !draft.trim()} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="gem-comments-submit">
              {replyTo ? "Post Reply" : "Post Comment"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function GemTabAccessDialog({ open, onOpenChange, users }) {
  const [grants, setGrants] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get("/gem-work/tab-access");
        const map = {};
        (data || []).forEach((g) => {
          const tabs = g.tabs || [];
          map[g.user_id] = {
            all: tabs.includes("all"),
            user_request: tabs.includes("user_request"),
            my_raised: tabs.includes("my_raised"),
            tender_found: tabs.includes("tender_found"),
            tender_working: tabs.includes("tender_working"),
            tender_filled: tabs.includes("tender_filled"),
            tender_unfilled: tabs.includes("tender_unfilled"),
            tender_result: tabs.includes("tender_result"),
            gem_credentials: tabs.includes("gem_credentials"),
          };
        });
        setGrants(map);
      } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    })();
  }, [open]);

  const toggle = (uid, key) => setGrants((g) => ({ ...g, [uid]: { ...(g[uid] || {}), [key]: !((g[uid] || {})[key]) } }));
  const save = async (uid) => {
    setBusy(true);
    try {
      const cur = grants[uid] || {};
      const tabs = [];
      ["all", "user_request", "my_raised", "tender_found", "tender_working", "tender_filled", "tender_unfilled", "tender_result", "gem_credentials"].forEach((k) => {
        if (cur[k]) tabs.push(k);
      });
      await api.post("/gem-work/tab-access", { user_id: uid, tabs });
      toast.success("Saved");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[88vh] overflow-y-auto" data-testid="gem-tab-access-dialog">
        <DialogHeader><DialogTitle className="heading">GeM Work · Tab Access</DialogTitle></DialogHeader>
        <p className="text-[11.5px] text-slate-600 mb-2">
          Master Admin grants per-user access to optional tabs. <strong>My Raised</strong> is always available to everyone.
        </p>
        <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50/60 border-b border-slate-200">
            <tr>
              <th className="px-3 py-2 text-left" rowSpan={2}>User</th>
              <th className="px-3 py-2 text-center bg-indigo-50/60" colSpan={2}>GeM Requests</th>
              <th className="px-3 py-2 text-center bg-emerald-50/60" colSpan={5}>Tender Work (sub-tabs)</th>
              <th className="px-3 py-2 text-center bg-rose-50/60" rowSpan={2}>Credentials Vault</th>
              <th className="px-3 py-2 text-right" rowSpan={2}>—</th>
            </tr>
            <tr className="border-t border-slate-200">
              <th className="px-2 py-1.5 text-center bg-indigo-50/30 text-[10px]">All Requests</th>
              <th className="px-2 py-1.5 text-center bg-indigo-50/30 text-[10px]">User Request</th>
              <th className="px-2 py-1.5 text-center bg-emerald-50/30 text-[10px]">Found</th>
              <th className="px-2 py-1.5 text-center bg-emerald-50/30 text-[10px]">Working</th>
              <th className="px-2 py-1.5 text-center bg-emerald-50/30 text-[10px]">Filled</th>
              <th className="px-2 py-1.5 text-center bg-emerald-50/30 text-[10px]">Unfilled</th>
              <th className="px-2 py-1.5 text-center bg-emerald-50/30 text-[10px]">Result</th>
            </tr>
          </thead>
          <tbody>
            {(users || []).length === 0 && (<tr><td colSpan={10} className="text-center text-slate-400 py-6">No users.</td></tr>)}
            {(users || []).map((u) => {
              const g = grants[u.id] || {};
              return (
                <tr key={u.id} className="border-t" data-testid={`gem-tab-access-row-${u.id}`}>
                  <td className="px-3 py-2"><div className="font-semibold text-slate-800">{u.name}</div><div className="text-[10px] text-slate-500">{u.email}</div></td>
                  <td className="px-2 py-2 text-center"><input type="checkbox" checked={!!g.all} onChange={() => toggle(u.id, "all")} data-testid={`gem-tab-access-all-${u.id}`} /></td>
                  <td className="px-2 py-2 text-center"><input type="checkbox" checked={!!g.user_request} onChange={() => toggle(u.id, "user_request")} data-testid={`gem-tab-access-userreq-${u.id}`} /></td>
                  <td className="px-2 py-2 text-center"><input type="checkbox" checked={!!g.tender_found} onChange={() => toggle(u.id, "tender_found")} data-testid={`tender-tab-access-found-${u.id}`} /></td>
                  <td className="px-2 py-2 text-center"><input type="checkbox" checked={!!g.tender_working} onChange={() => toggle(u.id, "tender_working")} data-testid={`tender-tab-access-working-${u.id}`} /></td>
                  <td className="px-2 py-2 text-center"><input type="checkbox" checked={!!g.tender_filled} onChange={() => toggle(u.id, "tender_filled")} data-testid={`tender-tab-access-filled-${u.id}`} /></td>
                  <td className="px-2 py-2 text-center"><input type="checkbox" checked={!!g.tender_unfilled} onChange={() => toggle(u.id, "tender_unfilled")} data-testid={`tender-tab-access-unfilled-${u.id}`} /></td>
                  <td className="px-2 py-2 text-center"><input type="checkbox" checked={!!g.tender_result} onChange={() => toggle(u.id, "tender_result")} data-testid={`tender-tab-access-result-${u.id}`} /></td>
                  <td className="px-2 py-2 text-center"><input type="checkbox" checked={!!g.gem_credentials} onChange={() => toggle(u.id, "gem_credentials")} data-testid={`gem-tab-access-creds-${u.id}`} /></td>
                  <td className="px-3 py-2 text-right"><Button size="sm" variant="outline" className="h-7 text-[10.5px]" disabled={busy} onClick={() => save(u.id)} data-testid={`gem-tab-access-save-${u.id}`}>Save</Button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// FileText kept for re-use if needed
export { FileText as _FileTextIcon };

/* ============================================================================
 * TENDER WORK section — embedded inside the GeM Work page as a sibling tab.
 * Master Admin and any user with at least one tender_* sub-tab access can
 * see this section. Inside, sub-tabs (Found / Working / Filled) are gated
 * individually by the same gem_tab_access mechanism.
 *
 * Lifecycle: Found (default) -> Working -> Filled (LOCKED — Master only).
 * Each tender auto-spawns a folder inside the configured Google Shared Drive
 * (folder name = tender_id), returning a clickable URL.
 * ==========================================================================*/

const TENDER_STAGE_TONE = {
  Found: "bg-amber-500 text-white",
  Working: "bg-blue-600 text-white",
  Filled: "bg-emerald-600 text-white",
  Unfilled: "bg-slate-700 text-white",
};

const RESULT_STATUS_TONE = {
  Open: "bg-amber-100 text-amber-800",
  Closed: "bg-emerald-100 text-emerald-800",
};

function TenderWorkSection({ isMaster, access }) {
  const subTabs = [
    { value: "Found", label: "Found", visible: isMaster || access.found, tone: "amber" },
    { value: "Working", label: "Working", visible: isMaster || access.working, tone: "blue" },
    { value: "Filled", label: "Filled", visible: isMaster || access.filled, tone: "emerald" },
    { value: "Unfilled", label: "Unfilled", visible: isMaster || access.unfilled, tone: "slate" },
    { value: "Result", label: "Result", visible: isMaster || access.result, tone: "indigo" },
  ].filter((t) => t.visible);
  const initial = subTabs[0]?.value || "Found";
  const [sub, setSub] = useState(initial);
  const [rows, setRows] = useState([]);
  const [newOpen, setNewOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [remarksTarget, setRemarksTarget] = useState(null);
  const [resultTarget, setResultTarget] = useState(null);

  const load = async (stage) => {
    try {
      const { data } = await api.get(`/tenders?stage=${encodeURIComponent(stage)}`);
      setRows(data || []);
    } catch (e) {
      setRows([]);
      // Silently swallow 403 (no access to this sub-tab) — UI already gates.
      if (e?.response?.status && e.response.status !== 403) {
        toast.error(formatError(e.response?.data?.detail));
      }
    }
  };

  useEffect(() => { load(sub); }, [sub]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => [r.tender_id, r.tender_url, r.created_by_name].some((v) => String(v || "").toLowerCase().includes(q)));
  }, [rows, search]);

  const onStageChange = async (r, newStage) => {
    if (!newStage || newStage === r.stage) return;
    setBusy(true);
    try {
      await api.patch(`/tenders/${r.id}/stage`, { stage: newStage });
      toast.success(`Tender marked ${newStage}`);
      load(sub);
    } catch (e) {
      const code = e?.response?.status;
      if (code === 423) toast.error("Locked — only Master Admin can change this tender.");
      else toast.error(formatError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const [pendingDeleteTender, setPendingDeleteTender] = useState(null);
  const onDelete = (r) => setPendingDeleteTender(r);
  const confirmDeleteTender = async (reason) => {
    try {
      await api.delete(`/tenders/${pendingDeleteTender.id}`, { data: { reason } });
      toast.success("Tender moved to Recycle Bin");
      load(sub);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); throw e; }
  };

  return (
    <div className="space-y-3">
      <DeleteWithReasonDialog
        open={!!pendingDeleteTender}
        onClose={() => setPendingDeleteTender(null)}
        title={pendingDeleteTender ? `Delete tender ${pendingDeleteTender.tender_id}?` : "Delete tender?"}
        description="Drive folder will NOT be deleted. Tender will move to Admin Hub → Recycle Bin; Master Admin can restore it later."
        onConfirm={confirmDeleteTender}
        testid="tender-delete"
      />
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex gap-1.5 flex-wrap">
          {subTabs.map((t) => (
            <button
              key={t.value}
              onClick={() => setSub(t.value)}
              className={`h-8 px-3 text-[11.5px] font-semibold rounded-full border transition-all ${sub === t.value ? (TENDER_STAGE_TONE[t.value] + " border-transparent") : "bg-white border-slate-200 text-slate-700 hover:border-slate-300"}`}
              data-testid={`tender-sub-${t.value.toLowerCase()}`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search tender id / url…" className="h-9 max-w-xs" data-testid="tender-search" />
          <Button onClick={() => setNewOpen(true)} className="h-9 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="tender-new-btn">
            <FolderPlus size={14} className="mr-1.5" /> New Tender
          </Button>
        </div>
      </div>

      <div className="pk-card overflow-x-auto">
        <table className="w-full text-sm responsive-table">
          <thead className="border-b border-slate-200 bg-slate-50/60">
            <tr>
              <th className="px-4 py-3 text-left pk-table-header">Tender ID</th>
              <th className="px-4 py-3 text-left pk-table-header">Tender URL</th>
              <th className="px-4 py-3 text-left pk-table-header">Doc Folder</th>
              <th className="px-4 py-3 text-left pk-table-header">Stage</th>
              {sub === "Result" && <th className="px-4 py-3 text-left pk-table-header">Result</th>}
              <th className="px-4 py-3 text-left pk-table-header">Raised By</th>
              <th className="px-4 py-3 text-left pk-table-header">Created</th>
              <th className="px-4 py-3 text-right pk-table-header">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredRows.length === 0 && (<tr><td colSpan={sub === "Result" ? 8 : 7} className="text-center py-10 text-slate-400 text-sm">No tenders here.</td></tr>)}
            {filteredRows.map((r) => {
              const isTerminal = r.stage === "Filled" || r.stage === "Unfilled";
              const resultRecorded = !!r.stage_locked_by_result;
              const locked = resultRecorded || (isTerminal && !isMaster);
              const nextStages = ["Found", "Working", "Filled", "Unfilled"].filter((s) => s !== r.stage);
              const remarkCount = (r.logs || []).filter((l) => l.action === "remark").length;
              const resultStatus = r.result_status || "";
              return (
                <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`tender-row-${r.id}`}>
                  <td className="px-4 py-3 font-semibold text-slate-900" data-label="Tender ID">{r.tender_id}</td>
                  <td className="px-4 py-3" data-label="URL">
                    {r.tender_url ? (
                      <a href={r.tender_url} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline inline-flex items-center gap-1 text-[12px]">
                        <ExternalLink size={11} /> Open
                      </a>
                    ) : <span className="text-slate-300 text-[12px]">—</span>}
                  </td>
                  <td className="px-4 py-3" data-label="Folder">
                    {r.tender_doc_folder_url ? (
                      <a href={r.tender_doc_folder_url} target="_blank" rel="noreferrer" className="text-emerald-700 hover:underline inline-flex items-center gap-1 text-[12px]" data-testid={`tender-folder-${r.id}`}>
                        <ExternalLink size={11} /> Drive
                      </a>
                    ) : <span className="text-slate-300 text-[12px]">—</span>}
                  </td>
                  <td className="px-4 py-3" data-label="Stage">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className={(TENDER_STAGE_TONE[r.stage] || "bg-slate-100 text-slate-700") + " text-[10px]"}>{r.stage}</Badge>
                      {!locked && (
                        <select
                          value=""
                          onChange={(e) => { const v = e.target.value; if (v) { onStageChange(r, v); e.target.value = ""; } }}
                          disabled={busy}
                          className="h-7 text-[10.5px] border border-slate-200 rounded-md px-1.5 bg-white dark:bg-slate-900 dark:border-slate-700 disabled:opacity-50"
                          data-testid={`tender-stage-${r.id}`}
                        >
                          <option value="">Change…</option>
                          {nextStages.map((s) => (<option key={s} value={s}>Mark {s}</option>))}
                        </select>
                      )}
                      {locked && <span className="text-[10px] text-slate-500" title={resultRecorded ? "Locked — result has been recorded" : undefined}>🔒 {resultRecorded ? "Result locked" : "Locked"}</span>}
                    </div>
                  </td>
                  {sub === "Result" && (
                    <td className="px-4 py-3" data-label="Result">
                      {resultStatus ? (
                        <Badge className={(RESULT_STATUS_TONE[resultStatus] || "bg-slate-100 text-slate-700") + " text-[10px] hover:opacity-90"}>{resultStatus}</Badge>
                      ) : <span className="text-slate-300 text-[12px]">—</span>}
                      {r.tech_eval_status && (
                        <div className="text-[10px] mt-0.5 text-slate-600">
                          Tech: <span className={r.tech_eval_status === "Qualified" ? "text-emerald-700 font-semibold" : "text-rose-700 font-semibold"}>{r.tech_eval_status}</span>
                          {r.financial_eval_awarded === true && <span className="ml-1 text-emerald-700 font-semibold">· Awarded</span>}
                          {r.financial_eval_awarded === false && <span className="ml-1 text-rose-700 font-semibold">· Not Awarded</span>}
                        </div>
                      )}
                    </td>
                  )}
                  <td className="px-4 py-3 text-[12px]" data-label="Raised By">{r.created_by_name || "—"}</td>
                  <td className="px-4 py-3 text-[12px] mono text-slate-600" data-label="Created">{r.created_at ? new Date(r.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—"}</td>
                  <td className="px-4 py-3 text-right" data-label="Actions">
                    <div className="flex items-center justify-end gap-1.5 flex-wrap">
                      <Button size="sm" variant="outline" className="h-8 px-2 text-[11px]" onClick={() => setRemarksTarget(r)} data-testid={`tender-remarks-${r.id}`}>
                        <FileText size={11} className="mr-1" /> Logs{remarkCount ? ` (${remarkCount})` : ""}
                      </Button>
                      {(sub === "Result" || r.stage === "Filled") && (
                        <Button size="sm" variant="outline" className="h-8 px-2 text-[11px] bg-indigo-50 border-indigo-200 text-indigo-800 hover:bg-indigo-100" onClick={() => setResultTarget(r)} data-testid={`tender-result-${r.id}`}>
                          Result
                        </Button>
                      )}
                      {isMaster && (
                        <Button size="sm" variant="outline" className="h-8 px-2 text-[11px] text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => onDelete(r)} data-testid={`tender-delete-${r.id}`}>
                          <Trash2 size={11} />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <NewTenderDialog open={newOpen} onOpenChange={setNewOpen} onCreated={() => load(sub)} />
      <TenderRemarksDialog target={remarksTarget} onOpenChange={(o) => { if (!o) setRemarksTarget(null); }} onUpdated={(updated) => { setRemarksTarget(updated); load(sub); }} />
      <TenderResultDialog target={resultTarget} onOpenChange={(o) => { if (!o) setResultTarget(null); }} onUpdated={(updated) => { setResultTarget(updated); load(sub); }} />
    </div>
  );
}

function NewTenderDialog({ open, onOpenChange, onCreated }) {
  const [tenderId, setTenderId] = useState("");
  const [tenderUrl, setTenderUrl] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (!open) { setTenderId(""); setTenderUrl(""); } }, [open]);

  const submit = async () => {
    const tid = tenderId.trim();
    if (!tid) { toast.error("Tender ID is required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/tenders", { tender_id: tid, tender_url: tenderUrl.trim() });
      toast.success(`Tender ${data.tender_id} created · Drive folder ready`);
      onCreated?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="tender-new-dialog">
        <DialogHeader>
          <DialogTitle className="heading flex items-center gap-2"><FolderPlus size={18} className="text-emerald-600" /> New Tender</DialogTitle>
          <div className="text-[11px] text-slate-500 mt-1">A Google Shared Drive folder will be auto-created with the Tender ID as the folder name. Default stage: <strong>Found</strong>.</div>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div>
            <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Tender ID *</Label>
            <Input value={tenderId} onChange={(e) => setTenderId(e.target.value)} placeholder="e.g. GEM/2026/B-1234567" className="mt-1.5 h-9 text-sm" data-testid="tender-new-id" />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Tender URL</Label>
            <Input value={tenderUrl} onChange={(e) => setTenderUrl(e.target.value)} placeholder="https://gem.gov.in/…" className="mt-1.5 h-9 text-sm" data-testid="tender-new-url" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="tender-new-submit">
            {busy ? "Creating folder…" : "Create Tender"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ============================================================================
 * GeM CREDENTIALS VAULT — embedded inside the GeM Work page.
 * Master Admin + users granted `gem_credentials` tab access can manage GeM
 * portal logins. Sensitive values (password) are encrypted at rest on the
 * server. UI shows masked values by default; the per-row "Reveal" toggle
 * fetches the plaintext via a dedicated endpoint (with audit logging).
 * ==========================================================================*/
function GemCredentialsSection({ isMaster }) {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  // Per-row revealed-field state: { [rowId]: { data: {...}, field: "gem_id"|"email"|"mobile"|"password" | null } }
  // Only ONE field can be revealed at a time per row. Clicking the eye on a
  // different field automatically hides the previously revealed one.
  const [revealMap, setRevealMap] = useState({});
  const [busyField, setBusyField] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/gem-credentials");
      setRows(data || []);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => [r.gem_id_masked, r.label, r.email_masked, r.mobile_masked, r.created_by_name].some((v) => String(v || "").toLowerCase().includes(q)));
  }, [rows, search]);

  /** Reveal exactly ONE field on a row. Clicking the same field hides it;
   * clicking a different field auto-hides the previous one. Each fetch is
   * audited server-side. */
  const toggleField = async (r, field) => {
    const cur = revealMap[r.id];
    if (cur?.field === field) {
      // Same field clicked again → hide it entirely
      setRevealMap((m) => ({ ...m, [r.id]: { ...m[r.id], field: null } }));
      return;
    }
    setBusyField(`${r.id}:${field}`);
    try {
      let data = cur?.data;
      if (!data) {
        const res = await api.get(`/gem-credentials/${r.id}/reveal`);
        data = res.data;
      }
      setRevealMap((m) => ({ ...m, [r.id]: { data, field } }));
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally { setBusyField(""); }
  };

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text || "");
      toast.success(`${label} copied`);
    } catch { toast.error("Copy failed"); }
  };

  const onDelete = async (r) => {
    if (!window.confirm(`Delete credential "${r.gem_id}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/gem-credentials/${r.id}`);
      toast.success("Credential deleted");
      setRevealMap((m) => { const nm = { ...m }; delete nm[r.id]; return nm; });
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-3">
      <div className="rounded-md border border-rose-200 bg-rose-50/40 px-3 py-2 text-[11.5px] text-rose-900 flex items-start gap-2">
        <KeyRound size={14} className="mt-0.5 shrink-0" />
        <div>
          <strong>Sensitive vault.</strong> Every field is hidden by default. Click the eye on a single field to reveal only that field — opening another field will automatically hide the previous one, so you can never see two values at once. Every reveal is logged with the user &amp; timestamp.
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by label, email…" className="h-9 max-w-sm" data-testid="creds-search" />
        <Button onClick={() => setAddOpen(true)} className="h-9 bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="creds-add-btn">
          <KeyRound size={14} className="mr-1.5" /> Add Credential
        </Button>
      </div>

      <div className="pk-card overflow-x-auto">
        <table className="w-full text-sm responsive-table">
          <thead className="border-b border-slate-200 bg-slate-50/60">
            <tr>
              <th className="px-4 py-3 text-left pk-table-header">GeM ID</th>
              <th className="px-4 py-3 text-left pk-table-header">Label</th>
              <th className="px-4 py-3 text-left pk-table-header">Email</th>
              <th className="px-4 py-3 text-left pk-table-header">Mobile</th>
              <th className="px-4 py-3 text-left pk-table-header">Password</th>
              <th className="px-4 py-3 text-left pk-table-header">Added By</th>
              <th className="px-4 py-3 text-right pk-table-header">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (<tr><td colSpan={7} className="text-center py-10 text-slate-400 text-sm">No credentials stored yet.</td></tr>)}
            {filtered.map((r) => {
              const rState = revealMap[r.id];
              const cur = rState?.field || null;
              return (
                <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`creds-row-${r.id}`}>
                  <td className="px-4 py-3 text-[12px] mono" data-label="GeM ID">
                    <SecretCell
                      revealed={cur === "gem_id"}
                      value={rState?.data?.gem_id}
                      mask={r.gem_id_masked}
                      busy={busyField === `${r.id}:gem_id`}
                      onToggle={() => toggleField(r, "gem_id")}
                      onCopy={() => copy(rState?.data?.gem_id, "GeM ID")}
                      testid={`creds-gemid-${r.id}`}
                    />
                  </td>
                  <td className="px-4 py-3 text-[12px] text-slate-700" data-label="Label">{r.label || <span className="text-slate-300">—</span>}</td>
                  <td className="px-4 py-3 text-[12px] mono" data-label="Email">
                    <SecretCell
                      revealed={cur === "email"}
                      value={rState?.data?.email}
                      mask={r.email_masked}
                      busy={busyField === `${r.id}:email`}
                      onToggle={() => toggleField(r, "email")}
                      onCopy={() => copy(rState?.data?.email, "Email")}
                      testid={`creds-email-${r.id}`}
                    />
                  </td>
                  <td className="px-4 py-3 text-[12px] mono" data-label="Mobile">
                    <SecretCell
                      revealed={cur === "mobile"}
                      value={rState?.data?.mobile}
                      mask={r.mobile_masked}
                      busy={busyField === `${r.id}:mobile`}
                      onToggle={() => toggleField(r, "mobile")}
                      onCopy={() => copy(rState?.data?.mobile, "Mobile")}
                      testid={`creds-mobile-${r.id}`}
                    />
                  </td>
                  <td className="px-4 py-3 text-[12px] mono" data-label="Password">
                    <SecretCell
                      revealed={cur === "password"}
                      value={rState?.data?.password}
                      mask={r.password_masked}
                      busy={busyField === `${r.id}:password`}
                      onToggle={() => toggleField(r, "password")}
                      onCopy={() => copy(rState?.data?.password, "Password")}
                      testid={`creds-password-${r.id}`}
                      highlight
                    />
                  </td>
                  <td className="px-4 py-3 text-[12px] text-slate-700" data-label="Added By">
                    <div>{r.created_by_name || "—"}</div>
                    <div className="text-[10px] text-slate-500 mono">{r.created_at ? new Date(r.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : ""}</div>
                  </td>
                  <td className="px-4 py-3 text-right" data-label="Actions">
                    <div className="flex items-center justify-end gap-1.5 flex-wrap">
                      <Button size="sm" variant="outline" className="h-8 px-2 text-[11px]" onClick={() => setEditTarget(r)} data-testid={`creds-edit-${r.id}`}>
                        <Pencil size={11} />
                      </Button>
                      {isMaster && (
                        <Button size="sm" variant="outline" className="h-8 px-2 text-[11px] text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => onDelete(r)} data-testid={`creds-delete-${r.id}`}>
                          <Trash2 size={11} />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <CredentialDialog open={addOpen} onOpenChange={setAddOpen} onSaved={load} />
      <CredentialDialog open={!!editTarget} target={editTarget} onOpenChange={(o) => { if (!o) setEditTarget(null); }} onSaved={load} />
    </div>
  );
}

/**
 * SecretCell — renders a single masked/revealed value with its own eye toggle
 * and copy button. Used in the Credentials table so each field (email,
 * mobile, password) reveals independently.
 */
function SecretCell({ revealed, value, mask, busy, onToggle, onCopy, testid, highlight }) {
  const isEmpty = revealed && !value;
  return (
    <div className="inline-flex items-center gap-1.5" data-testid={testid}>
      {revealed ? (
        <span className={`${highlight ? "inline-flex items-center gap-1.5 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded text-amber-900" : ""}`}>
          {isEmpty ? <span className="text-slate-400 italic">— blank —</span> : value}
          {!isEmpty && (<button onClick={onCopy} className="hover:text-indigo-600 text-slate-500" title="Copy" data-testid={`${testid}-copy`}><Copy size={11} /></button>)}
        </span>
      ) : (
        <span className="text-slate-500">{mask || <span className="text-slate-300">—</span>}</span>
      )}
      <button
        onClick={onToggle}
        disabled={busy}
        className="text-slate-400 hover:text-indigo-600 disabled:opacity-50"
        title={revealed ? "Hide" : "Reveal"}
        data-testid={`${testid}-toggle`}
      >
        {revealed ? <EyeOff size={12} /> : <Eye size={12} />}
      </button>
    </div>
  );
}

function CredentialDialog({ open, onOpenChange, target, onSaved }) {
  const editing = !!target;
  const [gemId, setGemId] = useState("");
  const [label, setLabel] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (target) {
      // For editing, load reveal first so the form is prefilled. Password stays
      // blank — user only updates it if they explicitly type something.
      (async () => {
        try {
          const { data } = await api.get(`/gem-credentials/${target.id}/reveal`);
          setGemId(data.gem_id || "");
          setLabel(data.label || "");
          setEmail(data.email || "");
          setMobile(data.mobile || "");
          setPassword("");
        } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
      })();
    } else {
      setGemId(""); setLabel(""); setEmail(""); setMobile(""); setPassword("");
    }
    setShow(false);
  }, [open, target]);

  const submit = async () => {
    if (!gemId.trim()) { toast.error("GeM ID is required"); return; }
    if (!editing && !password.trim()) { toast.error("Password is required"); return; }
    setBusy(true);
    try {
      const payload = { gem_id: gemId.trim(), label: label.trim(), email: email.trim(), mobile: mobile.trim() };
      if (password.trim()) payload.password = password.trim();
      if (editing) {
        await api.patch(`/gem-credentials/${target.id}`, payload);
        toast.success("Credential updated");
      } else {
        await api.post("/gem-credentials", payload);
        toast.success("Credential saved");
      }
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="creds-dialog">
        <DialogHeader>
          <DialogTitle className="heading flex items-center gap-2"><KeyRound size={18} className="text-indigo-600" /> {editing ? "Edit Credential" : "Add GeM Credential"}</DialogTitle>
          <div className="text-[11px] text-slate-500 mt-1">Password is encrypted at rest. {editing ? "Leave password blank to keep the existing one." : "All fields except Label are required."}</div>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div>
            <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">GeM ID *</Label>
            <Input value={gemId} onChange={(e) => setGemId(e.target.value)} className="mt-1.5 h-9 text-sm" data-testid="creds-input-gemid" />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Label (optional)</Label>
            <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Primary account, Branch 2…" className="mt-1.5 h-9 text-sm" data-testid="creds-input-label" />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Email</Label>
            <Input value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 h-9 text-sm" data-testid="creds-input-email" />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Mobile</Label>
            <Input value={mobile} onChange={(e) => setMobile(e.target.value)} className="mt-1.5 h-9 text-sm" data-testid="creds-input-mobile" />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Password {editing ? "" : "*"}</Label>
            <div className="mt-1.5 relative">
              <Input
                type={show ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={editing ? "Leave blank to keep existing" : "Enter password"}
                className="h-9 text-sm pr-9"
                data-testid="creds-input-password"
              />
              <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700" title={show ? "Hide" : "Show"}>
                {show ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="creds-submit">
            {busy ? "Saving…" : (editing ? "Save Changes" : "Save Credential")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ============================================================================
 * TenderRemarksDialog — unified Activity Log + add-remark composer for a
 * single tender. Renders all log entries (creation, stage changes, remarks,
 * result updates) in reverse-chronological order with the actor + timestamp.
 * ==========================================================================*/
function TenderRemarksDialog({ target, onOpenChange, onUpdated }) {
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const open = !!target;
  useEffect(() => { if (!open) setDraft(""); }, [open]);
  if (!open) return null;

  const logs = (target.logs || []).slice().reverse();
  const submit = async () => {
    const text = draft.trim();
    if (!text) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/tenders/${target.id}/remark`, { remark: text });
      toast.success("Remark added");
      setDraft("");
      onUpdated?.(data);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const labelFor = (a) => ({ created: "Created", stage_change: "Stage changed", remark: "Remark", result_update: "Result updated" }[a] || a);
  const toneFor = (a) => ({ created: "bg-slate-100 text-slate-700", stage_change: "bg-blue-100 text-blue-700", remark: "bg-amber-100 text-amber-800", result_update: "bg-indigo-100 text-indigo-700" }[a] || "bg-slate-100 text-slate-700");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="tender-remarks-dialog">
        <DialogHeader>
          <DialogTitle className="heading flex items-center gap-2"><FileText size={18} className="text-indigo-600" /> Tender Logs · {target.tender_id}</DialogTitle>
          <div className="text-[11px] text-slate-500 mt-1">Stage: <strong>{target.stage}</strong> · {logs.length} log entr{logs.length === 1 ? "y" : "ies"}</div>
        </DialogHeader>

        <div className="space-y-2 mt-2 max-h-[50vh] overflow-y-auto pr-1">
          {logs.length === 0 && (<div className="py-6 text-center text-xs text-slate-400 italic">No activity yet.</div>)}
          {logs.map((l, i) => (
            <div key={i} className="rounded-md border border-slate-200 bg-white px-3 py-2 text-[12px]" data-testid={`tender-log-${i}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="font-semibold text-slate-800">{l.by_name || "—"}</div>
                <Badge className={toneFor(l.action) + " text-[10px] hover:opacity-90"}>{labelFor(l.action)}</Badge>
              </div>
              <div className="text-[10.5px] text-slate-500 mono mt-0.5">{l.at ? new Date(l.at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—"}</div>
              {l.from !== undefined && l.to !== undefined && (
                <div className="text-[11.5px] mt-1">
                  <span className="text-rose-600 line-through">{l.from || "—"}</span> → <span className="text-emerald-700 font-semibold">{l.to || "—"}</span>
                </div>
              )}
              {Array.isArray(l.changes) && l.changes.length > 0 && (
                <ul className="text-[11.5px] mt-1 space-y-0.5">
                  {l.changes.map((c, j) => (
                    <li key={j}><span className="text-slate-500">{c.field}:</span> <span className="text-rose-600 line-through">{String(c.from ?? "—")}</span> → <span className="text-emerald-700 font-semibold">{String(c.to ?? "—")}</span></li>
                  ))}
                </ul>
              )}
              {l.remark && <div className="text-[12.5px] text-slate-700 mt-1 whitespace-pre-wrap">{l.remark}</div>}
            </div>
          ))}
        </div>

        <div className="border-t pt-3 mt-3 space-y-2">
          <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Add remark</Label>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="e.g. Spoke to vendor, need bid document revision…"
            rows={3}
            className="w-full text-sm border border-slate-200 rounded-md px-3 py-2 bg-white dark:bg-slate-900 dark:border-slate-700"
            data-testid="tender-remark-input"
          />
          <div className="flex items-center justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
            <Button onClick={submit} disabled={busy || !draft.trim()} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="tender-remark-submit">
              {busy ? "Posting…" : "Post Remark"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ============================================================================
 * TenderResultDialog — Technical evaluation → Financial evaluation flow for
 * any Filled tender. Once the closing artefact is provided (Contract ID when
 * awarded, L1 URL when not awarded, or reason when not technically qualified)
 * the tender is auto-marked Closed; otherwise it stays an Open Case.
 * ==========================================================================*/
function TenderResultDialog({ target, onOpenChange, onUpdated }) {
  const open = !!target;
  const [tech, setTech] = useState("");
  const [techReason, setTechReason] = useState("");
  const [awarded, setAwarded] = useState(null);   // true | false | null
  const [contractId, setContractId] = useState("");
  const [ourPos, setOurPos] = useState("");
  const [l1Url, setL1Url] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !target) return;
    setTech(target.tech_eval_status || "");
    setTechReason(target.tech_eval_reason || "");
    setAwarded(target.financial_eval_awarded === true || target.financial_eval_awarded === false ? target.financial_eval_awarded : null);
    setContractId(target.contract_id || "");
    setOurPos(target.our_position || "");
    setL1Url(target.l1_contract_url || "");
  }, [open, target?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null;

  const submit = async () => {
    if (target.stage !== "Filled") { toast.error("Result is only available for Filled tenders"); return; }
    if (!tech) { toast.error("Please set the Technical Evaluation outcome"); return; }
    if (tech === "Not Qualified" && !techReason.trim()) { toast.error("Please provide a reason for Not Qualified"); return; }
    setBusy(true);
    try {
      const payload = {
        tech_eval_status: tech,
        tech_eval_reason: tech === "Not Qualified" ? techReason.trim() : "",
      };
      if (tech === "Qualified") {
        payload.financial_eval_awarded = awarded;
        if (awarded === true) {
          payload.contract_id = contractId.trim();
          payload.our_position = "";
          payload.l1_contract_url = "";
        } else if (awarded === false) {
          payload.contract_id = "";
          payload.our_position = ourPos.trim();
          payload.l1_contract_url = l1Url.trim();
        }
      } else {
        // Tech not qualified clears all financial fields.
        payload.financial_eval_awarded = null;
        payload.contract_id = "";
        payload.our_position = "";
        payload.l1_contract_url = "";
      }
      const { data } = await api.patch(`/tenders/${target.id}/result`, payload);
      toast.success(`Result saved · ${data.result_status || "Open"}`);
      onUpdated?.(data);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const isClosed = (() => {
    if (tech === "Not Qualified") return true;
    if (tech === "Qualified" && awarded === true && contractId.trim()) return true;
    if (tech === "Qualified" && awarded === false && l1Url.trim()) return true;
    return false;
  })();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="tender-result-dialog">
        <DialogHeader>
          <DialogTitle className="heading flex items-center gap-2">Tender Result · {target.tender_id}</DialogTitle>
          <div className="text-[11px] text-slate-500 mt-1">Stage: <strong>{target.stage}</strong>{target.tender_doc_folder_url && (<> · <a href={target.tender_doc_folder_url} target="_blank" rel="noreferrer" className="text-emerald-700 hover:underline inline-flex items-center gap-1"><ExternalLink size={10} /> Open Drive folder</a></>)}</div>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {target.stage !== "Filled" && (
            <div className="text-[12px] bg-amber-50 border border-amber-200 text-amber-900 rounded-md px-3 py-2">
              The Result lifecycle activates only after the tender is marked <strong>Filled</strong>.
            </div>
          )}

          <div>
            <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Technical Evaluation</Label>
            <div className="mt-1.5 flex gap-2">
              {["Qualified", "Not Qualified"].map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setTech(k)}
                  disabled={target.stage !== "Filled"}
                  className={`flex-1 h-10 text-sm font-semibold rounded-md border-2 transition-all disabled:opacity-50 ${tech === k ? (k === "Qualified" ? "bg-emerald-600 border-emerald-600 text-white" : "bg-rose-600 border-rose-600 text-white") : "bg-white border-slate-200 text-slate-700 hover:border-slate-300"}`}
                  data-testid={`tender-result-tech-${k.toLowerCase().replace(" ", "-")}`}
                >
                  {k}
                </button>
              ))}
            </div>
          </div>

          {tech === "Not Qualified" && (
            <div>
              <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Reason *</Label>
              <textarea
                value={techReason}
                onChange={(e) => setTechReason(e.target.value)}
                placeholder="Why were we not technically qualified?"
                rows={3}
                className="mt-1.5 w-full text-sm border border-slate-200 rounded-md px-3 py-2 bg-white dark:bg-slate-900 dark:border-slate-700"
                data-testid="tender-result-tech-reason"
              />
            </div>
          )}

          {tech === "Qualified" && (
            <>
              <div>
                <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Financial Evaluation · Is this Tender Awarded?</Label>
                <div className="mt-1.5 flex gap-2">
                  {[
                    { key: "yes", label: "Awarded — Yes", v: true, tone: "emerald" },
                    { key: "no", label: "Not Awarded — No", v: false, tone: "rose" },
                  ].map(({ key, label, v, tone }) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setAwarded(v)}
                      className={`flex-1 h-10 text-sm font-semibold rounded-md border-2 transition-all ${awarded === v ? (tone === "emerald" ? "bg-emerald-600 border-emerald-600 text-white" : "bg-rose-600 border-rose-600 text-white") : "bg-white border-slate-200 text-slate-700 hover:border-slate-300"}`}
                      data-testid={`tender-result-awarded-${key}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {awarded === true && (
                <div>
                  <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Contract ID *</Label>
                  <Input value={contractId} onChange={(e) => setContractId(e.target.value)} placeholder="e.g. GEMC-XXXXXX" className="mt-1.5 h-9 text-sm" data-testid="tender-result-contract-id" />
                </div>
              )}

              {awarded === false && (
                <>
                  <div>
                    <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Our Position (rank)</Label>
                    <Input value={ourPos} onChange={(e) => setOurPos(e.target.value)} placeholder="e.g. L2, L3…" className="mt-1.5 h-9 text-sm" data-testid="tender-result-our-position" />
                  </div>
                  <div>
                    <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">L1 Contract URL *</Label>
                    <div className="text-[11px] text-slate-500 mt-0.5">Upload the L1 winner&apos;s contract to this tender&apos;s Drive folder, then paste the file&apos;s share URL here.</div>
                    <Input value={l1Url} onChange={(e) => setL1Url(e.target.value)} placeholder="https://drive.google.com/file/d/…" className="mt-1.5 h-9 text-sm" data-testid="tender-result-l1-url" />
                  </div>
                </>
              )}
            </>
          )}

          {tech && (
            <div className="rounded-md border px-3 py-2 text-[12px]" style={{ backgroundColor: isClosed ? "rgb(236 253 245)" : "rgb(255 251 235)", borderColor: isClosed ? "rgb(167 243 208)" : "rgb(253 230 138)" }}>
              <span className="font-semibold">Outcome:</span> <Badge className={(isClosed ? "bg-emerald-600 text-white" : "bg-amber-500 text-white") + " text-[10px] ml-1"}>{isClosed ? "Will be Closed" : "Open Case"}</Badge>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy || target.stage !== "Filled"} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="tender-result-submit">
            {busy ? "Saving…" : "Save Result"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

